/**
 * Minimal CSV helpers — batch whitelist import (parse) + table export (build).
 *
 * Zero deps on purpose. The parser is a real CSV tokenizer (quoted fields may
 * contain commas / "" escapes / newlines) because operator CSVs come out of
 * Excel/Numbers exports — a naive line-split silently corrupts those. We only
 * consume column 0, but the whole record must still be tokenized correctly to
 * find the true record boundary.
 */

/** Tokenize CSV text into rows of fields (RFC-4180-ish: `""` escapes, quoted
 *  fields may span newlines, CRLF/CR/LF all accepted). Strips a leading BOM. */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const pushField = () => { row.push(field); field = ''; };
  const pushRow = () => { pushField(); rows.push(row); row = []; };
  while (i < src.length) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"' && field === '') { inQuotes = true; i++; continue; }
    if (c === ',') { pushField(); i++; continue; }
    if (c === '\r') { pushRow(); i += src[i + 1] === '\n' ? 2 : 1; continue; }
    if (c === '\n') { pushRow(); i++; continue; }
    field += c; i++;
  }
  // Flush the trailing record (no final newline). A single empty trailing
  // field from a file ending in \n never reaches here (pushRow consumed it).
  if (field !== '' || row.length > 0) pushRow();
  return rows;
}

/** First column of every row, raw (caller normalizes/validates/dedupes). */
export function parseCsvFirstColumn(text: string): string[] {
  return parseCsv(text).map((r) => r[0] ?? '');
}

/** Spreadsheet formula-injection guard: a cell starting with = + - @ (or tab / CR) is opened by
 *  Excel as a formula, so prefix it with an apostrophe. Mirrors the Worker's CSV exports. */
export function csvSafeCell(v: string): string {
  return /^[=+\-@\t\r]/.test(v) ? "'" + v : v;
}

/** RFC-4180 escaping for one cell (quotes when it holds a quote, comma or newline). */
export function csvEscape(v: string): string {
  return /[",\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}

/** Build CSV text from a header + string rows (RFC-4180 escaping, CRLF). Cells are
 *  formula-guarded by default — these files are opened in Excel by operators. */
export function buildCsv(header: string[], rows: string[][], opts: { formulaGuard?: boolean } = {}): string {
  const guard = opts.formulaGuard !== false;
  const cell = (s: string) => csvEscape(guard ? csvSafeCell(s) : s);
  return [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
}

/** Trigger a browser download of CSV text. The BOM makes Excel open UTF-8
 *  Chinese correctly (this SPA runs in a normal browser behind Access — the
 *  App-side `<a download>` prohibition is Electron-specific, not applicable). */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
